import { ReactElement, useMemo, useRef, useState } from 'react';
import { useCompaniesTranslation } from '../hooks';
import { useOpenCheckoutSessionAction } from '../actions';
import { useFetchSubscriptionPrices } from '../queries';
import { useCompanyContext } from '../providers';
import {
    UIEmptyState,
    UISpinner,
    UIButton,
    UIIcon,
    UIMenu,
    UIMenuItem,
    UIPopover,
    UIPropoverRef,
    UISegmentedControl2,
    UIText
} from '@/ui';
import { usePersistedState } from '@/common/hooks';
import { charcoal, concrete, fontColorLight, moonlight } from '@/ui/styles';
import { GetPricesResponseData, SubscriptionPrice } from '@/api';

type NewSubscriptionSettingsSectionProps = {
    canEdit: boolean;
};
export function NewSubscriptionSettingsSection(
    props: NewSubscriptionSettingsSectionProps
): ReactElement {
    const { canEdit } = props;
    const { t } = useCompaniesTranslation();
    const { company } = useCompanyContext();

    const { openCheckoutSession, loading } = useOpenCheckoutSessionAction({
        companyId: company.id
    });
    const { status: pricesStatus, data: pricesData } =
        useFetchSubscriptionPrices();

    if (!canEdit) {
        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: 400,
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 16
                }}
            >
                <UIEmptyState
                    title={t(
                        'components.new_subscription_settings_section.no_subscription.title'
                    )}
                    description={t(
                        'components.new_subscription_settings_section.no_subscription.description'
                    )}
                />
            </div>
        );
    }

    if (pricesStatus === 'pending') {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: 400
                }}
            >
                <UISpinner />
            </div>
        );
    }

    if (pricesStatus === 'error' || !pricesData) {
        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: 400,
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 16
                }}
            >
                <UIEmptyState
                    title={t(
                        'components.new_subscription_settings_section.unable_to_load.title'
                    )}
                    description={t(
                        'components.new_subscription_settings_section.unable_to_load.description'
                    )}
                />
            </div>
        );
    }

    return (
        <NewSubscriptionSettingsSectionForm
            pricesData={pricesData}
            onSubscribePress={(priceId, currency) => {
                openCheckoutSession(priceId, currency).catch(() => {}); // Error handled in the action
            }}
            loading={loading}
        />
    );
}

type NewSubscriptionSettingsSectionFormProps = {
    pricesData: GetPricesResponseData;
    onSubscribePress: (priceId: string, currency: string) => void;
    loading: boolean;
};

function formatPrice(amount: number, currency: string): string {
    return new Intl.NumberFormat('en', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2
    }).format(amount / 100);
}

function getCurrencySymbol(currency: string): string {
    return (
        new Intl.NumberFormat('en', {
            style: 'currency',
            currency: currency
        })
            .formatToParts(0)
            .find((part) => part.type === 'currency')?.value ?? currency
    );
}

function getPriceForPlan(
    prices: SubscriptionPrice[],
    planType: 'STANDARD' | 'GUARDIAN_PLUS',
    interval: 'month' | 'year',
    currency: string
): {
    stripePriceId: string;
    amount: number;
    isAvailable: boolean;
    unavailableUrl?: string;
} | null {
    const price = prices.find(
        (p) => p.planType === planType && p.interval === interval
    );
    if (!price) return null;

    const currencyPrice = price.currencyPrices.find(
        (c) => c.currency === currency
    );
    if (!currencyPrice) return null;

    return {
        stripePriceId: price.stripePriceId,
        amount: currencyPrice.amount,
        isAvailable: price.isAvailable,
        unavailableUrl: price.unavailableUrl
    };
}

function NewSubscriptionSettingsSectionForm(
    props: NewSubscriptionSettingsSectionFormProps
): ReactElement {
    const { pricesData, onSubscribePress, loading } = props;
    const { t } = useCompaniesTranslation();

    const [selectedIntervalIndex, setSelectedIntervalIndex] = useState(0);
    const popoverRef = useRef<UIPropoverRef>(null);

    const availableCurrencies = useMemo(() => {
        const currencySet = new Set<string>();
        pricesData.prices.forEach((price) => {
            price.currencyPrices.forEach((cp) => {
                currencySet.add(cp.currency);
            });
        });
        return Array.from(currencySet).sort();
    }, [pricesData.prices]);

    const [persistedCurrency, setPersistedCurrency] = usePersistedState<string>(
        'subscription_currency',
        pricesData.defaultCurrency
    );

    // Use persisted currency if available, otherwise fall back to first available
    const selectedCurrency = availableCurrencies.includes(persistedCurrency)
        ? persistedCurrency
        : (availableCurrencies[0] ?? 'GBP');

    const interval = selectedIntervalIndex === 0 ? 'month' : 'year';

    const handleCurrencySelect = (currency: string) => {
        setPersistedCurrency(currency);
        popoverRef.current?.close();
    };

    const standardPrice = getPriceForPlan(
        pricesData.prices,
        'STANDARD',
        interval,
        selectedCurrency
    );
    const guardianPlusPrice = getPriceForPlan(
        pricesData.prices,
        'GUARDIAN_PLUS',
        interval,
        selectedCurrency
    );

    const standardPriceString = standardPrice
        ? formatPrice(standardPrice.amount, selectedCurrency)
        : '—';
    const guardianPlusPriceString = guardianPlusPrice
        ? formatPrice(guardianPlusPrice.amount, selectedCurrency)
        : '—';

    const currencyTrigger = (
        <button
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: 4,
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: fontColorLight
            }}
        >
            <UIText variant="small">
                {getCurrencySymbol(selectedCurrency)} {selectedCurrency}
            </UIText>
            <UIIcon name="chevron-down" size={12} />
        </button>
    );

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: 32,
                border: `1px solid ${concrete}`,
                borderRadius: 8,
                gap: 24,
                maxWidth: 800,
                position: 'relative'
            }}
        >
            {availableCurrencies.length > 1 && (
                <div style={{ position: 'absolute', top: 12, right: 12 }}>
                    <UIPopover
                        ref={popoverRef}
                        triggerNode={currencyTrigger}
                        placement="bottom_right"
                        width={120}
                    >
                        <UIMenu>
                            {availableCurrencies.map((currency) => (
                                <UIMenuItem
                                    key={currency}
                                    text={`${getCurrencySymbol(currency)} ${currency}`}
                                    checkboxProps={{
                                        checked: selectedCurrency === currency,
                                        onChange: () =>
                                            handleCurrencySelect(currency)
                                    }}
                                    checkboxPosition="right"
                                    onClick={() =>
                                        handleCurrencySelect(currency)
                                    }
                                />
                            ))}
                        </UIMenu>
                    </UIPopover>
                </div>
            )}
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 8
                }}
            >
                <UIText variant="h1">
                    {t(
                        'components.new_subscription_settings_section.no_active.title'
                    )}
                </UIText>
                <UIText>
                    {t(
                        'components.new_subscription_settings_section.no_active.description'
                    )}
                </UIText>
            </div>
            <UISegmentedControl2
                items={[
                    {
                        text: t(
                            'components.new_subscription_settings_section.interval.month'
                        ),
                        width: 100
                    },
                    {
                        text: t(
                            'components.new_subscription_settings_section.interval.year'
                        ),
                        width: 100
                    }
                ]}
                selectedIndex={selectedIntervalIndex}
                selectedIndexChanged={setSelectedIntervalIndex}
            />

            <div
                style={{
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'row',
                    width: '100%',
                    gap: 24
                }}
            >
                <SubscriptionItem
                    planName={t(
                        'components.new_subscription_settings_section.plan.standard'
                    )}
                    price={standardPriceString}
                    interval={t(
                        `components.new_subscription_settings_section.interval.${interval}`
                    )}
                    features={[
                        t('shared.subscription.feature_unlimited_tasks'),
                        t('shared.subscription.feature_unlimited_teams'),
                        t('shared.subscription.feature_mobile_apps'),
                        t('shared.subscription.feature_sms_50')
                    ]}
                    loading={loading}
                    disabled={!standardPrice}
                    isAvailable={standardPrice?.isAvailable ?? true}
                    unavailableUrl={standardPrice?.unavailableUrl}
                    minLicenses={2}
                    minLicensesLabel={t(
                        'components.new_subscription_settings_section.licenses_minimum',
                        {
                            count: 2
                        }
                    )}
                    perLicenseLabel={t(
                        'components.new_subscription_settings_section.per_license',
                        {
                            interval: t(
                                `components.new_subscription_settings_section.interval.${interval}`
                            )
                        }
                    )}
                    includesLabel={t(
                        'components.new_subscription_settings_section.includes'
                    )}
                    badgeComingSoon={t(
                        'components.new_subscription_settings_section.badge.coming_soon'
                    )}
                    badgeMostPopular={t(
                        'components.new_subscription_settings_section.badge.most_popular'
                    )}
                    buttonNotifyMe={t(
                        'components.new_subscription_settings_section.button.notify_me'
                    )}
                    buttonSubscribe={t(
                        'components.new_subscription_settings_section.button.subscribe'
                    )}
                    onSubscribePress={() => {
                        if (standardPrice) {
                            onSubscribePress(
                                standardPrice.stripePriceId,
                                selectedCurrency
                            );
                        }
                    }}
                />
                <SubscriptionItem
                    planName={t(
                        'components.new_subscription_settings_section.plan.guardian_plus'
                    )}
                    price={guardianPlusPriceString}
                    interval={t(
                        `components.new_subscription_settings_section.interval.${interval}`
                    )}
                    features={[
                        t('shared.subscription.feature_unlimited_tasks'),
                        t('shared.subscription.feature_unlimited_teams'),
                        t('shared.subscription.feature_mobile_apps'),
                        t('shared.subscription.feature_sms_100'),
                        t('shared.subscription.feature_monitoring')
                    ]}
                    loading={loading}
                    disabled={!guardianPlusPrice}
                    isAvailable={guardianPlusPrice?.isAvailable ?? true}
                    unavailableUrl={guardianPlusPrice?.unavailableUrl}
                    perLicenseLabel={t(
                        'components.new_subscription_settings_section.per_license',
                        {
                            interval: t(
                                `components.new_subscription_settings_section.interval.${interval}`
                            )
                        }
                    )}
                    includesLabel={t(
                        'components.new_subscription_settings_section.includes'
                    )}
                    badgeComingSoon={t(
                        'components.new_subscription_settings_section.badge.coming_soon'
                    )}
                    badgeMostPopular={t(
                        'components.new_subscription_settings_section.badge.most_popular'
                    )}
                    buttonNotifyMe={t(
                        'components.new_subscription_settings_section.button.notify_me'
                    )}
                    buttonSubscribe={t(
                        'components.new_subscription_settings_section.button.subscribe'
                    )}
                    onSubscribePress={() => {
                        if (guardianPlusPrice) {
                            onSubscribePress(
                                guardianPlusPrice.stripePriceId,
                                selectedCurrency
                            );
                        }
                    }}
                    isMostPopular
                />
            </div>
        </div>
    );
}

type SubscriptionItemProps = {
    planName: string;
    price: string;
    interval: string;
    features: string[];
    loading: boolean;
    disabled?: boolean;
    isAvailable: boolean;
    unavailableUrl?: string;
    minLicenses?: number;
    minLicensesLabel?: string;
    perLicenseLabel: string;
    includesLabel: string;
    badgeComingSoon: string;
    badgeMostPopular: string;
    buttonNotifyMe: string;
    buttonSubscribe: string;
    onSubscribePress: () => void;
    isMostPopular?: boolean;
};
function SubscriptionItem(props: SubscriptionItemProps): ReactElement {
    const {
        planName,
        price,
        features,
        loading,
        disabled = false,
        isAvailable,
        unavailableUrl,
        minLicenses,
        minLicensesLabel,
        perLicenseLabel,
        includesLabel,
        badgeComingSoon,
        badgeMostPopular,
        buttonNotifyMe,
        buttonSubscribe,
        onSubscribePress,
        isMostPopular = false
    } = props;

    const badgeText = !isAvailable
        ? badgeComingSoon
        : isMostPopular
          ? badgeMostPopular
          : null;

    const renderButton = () => {
        if (!isAvailable && unavailableUrl) {
            return (
                <UIButton
                    variant="default"
                    onClick={() => window.open(unavailableUrl, '_blank')}
                >
                    {buttonNotifyMe}
                </UIButton>
            );
        }
        if (!isAvailable) {
            return (
                <UIButton variant="default" disabled>
                    {badgeComingSoon}
                </UIButton>
            );
        }
        return (
            <UIButton
                variant="primary"
                loading={loading}
                disabled={disabled}
                onClick={onSubscribePress}
            >
                {buttonSubscribe}
            </UIButton>
        );
    };

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                flex: 1,
                gap: 16,
                border: isMostPopular ? `1px solid ${concrete}` : undefined,
                borderRadius: 8,
                padding: 42
            }}
        >
            <div
                style={{
                    backgroundColor: moonlight,
                    padding: 8,
                    borderRadius: 4,
                    opacity: badgeText ? 1 : 0
                }}
            >
                <UIText type="default2">{badgeText ?? '.'}</UIText>
            </div>

            <UIText variant="h2">{planName}</UIText>

            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <UIText variant="h1">{price}</UIText>
                <UIText>{perLicenseLabel}</UIText>
                <UIText
                    variant="small"
                    style={{
                        marginTop: 10,
                        opacity: minLicenses && minLicenses > 1 ? 1 : 0
                    }}
                >
                    {minLicensesLabel ?? '.'}
                </UIText>
            </div>

            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%'
                }}
            >
                {renderButton()}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <UIText bold>{includesLabel}</UIText>
                {features.map((feature, index) => (
                    <div
                        key={index}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10
                        }}
                    >
                        <UIIcon name="check" size={16} color={charcoal} />
                        <UIText>{feature}</UIText>
                    </div>
                ))}
            </div>
        </div>
    );
}
